/**
 * Stacked Capacity Bar Component
 *
 * Displays a single horizontal stacked bar showing raw capacity with colored
 * segments "carved out" for losses (parity, padding, overhead, slop).
 *
 * This replaces the six separate progress bars with a single visual
 * explanation of "where the space went."
 */

import { Box, Tooltip, Typography, Paper } from '@mui/material';
import type { CapacityResults } from '../../types';

interface CapacitySegment {
  name: string;
  value: number; // TiB
  color: string;
  isLoss: boolean;
}

interface StackedCapacityBarProps {
  results: CapacityResults;
}

/**
 * StackedCapacityBar - Visualizes capacity losses in a single stacked bar
 *
 * Color coding:
 * - Red/Orange = Losses (Parity, Padding, Overhead)
 * - Green = Usable space
 */
export function StackedCapacityBar({ results }: StackedCapacityBarProps) {
  const segments: CapacitySegment[] = [
    { name: 'Parity', value: results.losses.parity, color: '#ef5350', isLoss: true },      // Light red
    { name: 'Padding', value: results.losses.padding, color: '#ffa726', isLoss: true },    // Orange
    { name: 'ZFS Overhead', value: results.losses.overhead, color: '#ffca28', isLoss: true }, // Amber
    { name: 'Slop Space', value: results.losses.slop, color: '#66bb6a', isLoss: true },    // Light green
    { name: 'Usable', value: results.finalUsableTiB, color: '#43a047', isLoss: false },    // Dark green
  ];

  const rawCapacity = results.rawTiB;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Capacity Breakdown by Component
      </Typography>

      {/* Stacked Bar */}
      <Box
        sx={{
          display: 'flex',
          height: 48,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          mb: 1.5,
        }}
      >
        {segments.map((segment, idx) => {
          const widthPercent = (segment.value / rawCapacity) * 100;
          const showLabel = widthPercent > 10;

          return (
            <Tooltip
              key={idx}
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="body2" component="div">
                    <strong>{segment.name}</strong>
                  </Typography>
                  <Typography variant="caption" component="div">
                    {segment.value.toFixed(2)} TiB ({widthPercent.toFixed(1)}%)
                  </Typography>
                </Box>
              }
              arrow
              placement="top"
            >
              <Box
                sx={{
                  width: `${widthPercent}%`,
                  bgcolor: segment.color,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'filter 0.2s',
                  position: 'relative',
                  '&:hover': {
                    filter: 'brightness(1.1)',
                    zIndex: 2,
                  },
                  // Show label for segments > 10%
                  ...(showLabel && {
                    '&::after': {
                      content: `'${segment.value.toFixed(1)}'`,
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                    }
                  })
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        {segments.map((segment, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                bgcolor: segment.color,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
            <Typography variant="caption">
              {segment.name}: <strong>{segment.value.toFixed(2)} TiB</strong>
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Color Key Explanation */}
      <Paper
        sx={{
          p: 1.5,
          bgcolor: 'info.dark',
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="white" sx={{ display: 'block' }}>
          <strong>Color Coding:</strong> Red/Orange = Loss (Parity, Padding, Overhead) |
          {' '}Light Green = Reserved (Slop Space) |
          {' '}Dark Green = Usable Space
        </Typography>
      </Paper>
    </Box>
  );
}
